import React, {useContext, useMemo} from "react";
import TextField from "@mui/material/TextField";
import makeStyles from "@mui/styles/makeStyles";
import {useTheme} from "@mui/material/styles";
import {Controller} from "react-hook-form";
import clsx from "clsx";
import {AppContext} from "../../../App";
import {languages} from "../../../strings/LanguageDictionary";

const useStyles = makeStyles((theme) => ({
	root: {
		"&&": {
			width: "100%",
			minWidth: 0,
		},
		"&& .MuiInputBase-root, && .MuiOutlinedInput-root": {
			minHeight: 40,
			borderRadius: "14px !important",
			background: `${theme.palette.mode === "light" ? "#fff" : "#18202c"} !important`,
			backgroundImage: "none !important",
			backgroundColor: `${theme.palette.mode === "light" ? "#fff" : "#18202c"} !important`,
			backgroundClip: "padding-box !important",
			backdropFilter: "none !important",
			WebkitBackdropFilter: "none !important",
		},
		"&& .MuiInputBase-input, && .MuiOutlinedInput-input": {
			background: "transparent !important",
			backgroundColor: "transparent !important",
		},
		"& .MuiOutlinedInput-notchedOutline": {
			borderColor: theme.palette.mode === "light" ? "rgba(32, 50, 64, 0.18)" : "rgba(255, 255, 255, 0.16)",
		},
		"& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
			borderColor: theme.palette.mode === "light" ? "rgba(47, 99, 255, 0.42)" : "rgba(129, 212, 250, 0.34)",
		},
		"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
			borderColor: theme.palette.mode === "light" ? "#2f63ff" : "rgba(129, 212, 250, 0.76)",
			borderWidth: "1px !important",
		},
		"& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
			borderColor: `${theme.palette.error.main} !important`,
			borderWidth: "1px !important",
		},
		"& .MuiInputLabel-root": {
			fontWeight: 800,
		},
		"& .MuiInputLabel-root.Mui-error": {
			color: `${theme.palette.error.main} !important`,
		},
		"& .MuiFormHelperText-root": {
			position: "static !important",
			margin: `${theme.spacing(0.45)} 0 0 ${theme.spacing(1.25)} !important`,
			padding: "0 !important",
			color: `${theme.palette.error.main} !important`,
			background: "transparent !important",
			borderRadius: "0 !important",
			boxShadow: "none !important",
			fontSize: "12px !important",
			fontWeight: "750 !important",
			lineHeight: "1.25 !important",
			transform: "none !important",
		},
	},
}));

const getSolidBackground = (theme) => theme.palette.mode === "light" ? "#fff" : "#18202c";

const SolidTextField = ({
	autoComplete,
	autoFocus,
	className = "",
	control,
	defaultValue = "",
	disabled,
	error,
	fullWidth = true,
	handleKeyDown = false,
	handleKeyPress = false,
	helperText,
	inputProps,
	label,
	margin = "dense",
	multiline = false,
	name,
	onBlur,
	onChange,
	placeholder,
	rows,
	rules,
	size = "medium",
	slotProps = {},
	type = "text",
	value,
	variant = "outlined",
}) => {
	const classes = useStyles();
	const theme = useTheme();
	const appContext = useContext(AppContext);
	const solidBackground = getSolidBackground(theme);
	const solidInputStyle = useMemo(() => ({
		background: solidBackground,
		backgroundColor: solidBackground,
		backgroundImage: "none",
		backgroundClip: "padding-box",
		backdropFilter: "none",
		WebkitBackdropFilter: "none",
	}), [solidBackground]);
	const helperTextStyle = useMemo(() => ({
		position: "static",
		margin: `${theme.spacing(0.45)} 0 0 ${theme.spacing(1.25)}`,
		padding: 0,
		color: theme.palette.error.main,
		background: "transparent",
		borderRadius: 0,
		boxShadow: "none",
		fontSize: 12,
		fontWeight: 750,
		lineHeight: 1.25,
		transform: "none",
	}), [theme]);
	const resolvedHelperText = helperText || (
		error?.type === "validate"
			? languages[appContext.state.lang].OCEAN__GLOBAL_NOT_VALID_FILES
			: error?.message ? languages[appContext.state.lang].OCEAN__GLOBAL_REQUIRED_FILES_ : ""
	);

	const renderField = (fieldValue, handleChange) => {
		const hasControlledValue = fieldValue !== undefined || value !== undefined;
		const valueProps = hasControlledValue
			? {value: fieldValue !== undefined ? (fieldValue ?? "") : (value ?? "")}
			: {defaultValue};

		return (
			<TextField
				{...valueProps}
				autoComplete={autoComplete}
				autoFocus={autoFocus}
				className={clsx(classes.root, className)}
				data-solid-text-field="true"
				disabled={disabled}
				error={Boolean(error)}
				fullWidth={fullWidth}
				helperText={resolvedHelperText}
				id={name}
				label={label}
				margin={margin}
				multiline={multiline}
				onBlur={onBlur}
				onChange={(event) => {
					handleChange?.(event);
					onChange?.(event);
				}}
				onKeyDown={(event) => handleKeyDown ? handleKeyDown(event) : undefined}
				onKeyPress={(event) => handleKeyPress ? handleKeyPress(event) : undefined}
				placeholder={placeholder}
				rows={rows}
				size={size}
				slotProps={{
					...slotProps,
					input: {
						...slotProps.input,
						style: {
							...solidInputStyle,
							...slotProps.input?.style,
						},
					},
					htmlInput: {
						...slotProps.htmlInput,
						...inputProps,
						style: {
							background: "transparent",
							backgroundColor: "transparent",
							...inputProps?.style,
							...slotProps.htmlInput?.style,
						},
					},
					formHelperText: {
						...slotProps.formHelperText,
						style: {
							...helperTextStyle,
							...slotProps.formHelperText?.style,
						},
					},
				}}
				type={type}
				variant={variant}
			/>
		);
	};

	if (control && name) {
		return (
			<Controller
				name={name}
				control={control}
				defaultValue={defaultValue}
				rules={rules}
				render={({onChange: controllerOnChange, value: controllerValue}) => (
					renderField(controllerValue, controllerOnChange)
				)}
			/>
		);
	}

	return renderField(undefined);
};

export default React.memo(SolidTextField);
